
import { Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { IsRoot } from '@capgo/capacitor-is-root';
import { AvailableResult, BiometryType, Credentials, NativeBiometric } from "@capgo/capacitor-native-biometric";
import { AlertController } from '@ionic/angular';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class BiometricService {
 
	constructor(
	)
	{
	}

	private biometricAvailableResult: AvailableResult | null = null;
	public async fetchBiometricAvailability(): Promise<AvailableResult | null>
	{
		try
		{
			const result = await NativeBiometric.isAvailable();
			console.debug('Biometric availability result:', result);
			this.biometricAvailableResult = result;
			return result;
		}
		catch (error)
		{
			console.error("Error checking biometric availability", error);
			return null;
		}
	}

	public async tryBiometricVerifyIdentity(): Promise<boolean>
	{
		// 2. Perform biometric authentication
		try
		{
			await NativeBiometric.verifyIdentity({
				reason: "Authenticate to access your account",
				title: "Biometric login",
			});
			return true;
		} 
		catch (error)
		{
			console.error("Biometric authentication failed", error);
			return false; // Authentication failed, return false or handle as needed
		}
	}

	public async fetchUserCredentialsWithBiometrics(server: string): Promise<Credentials | null>
	{
		const biometricStatus = await this.fetchBiometricAvailability();

		if (!biometricStatus || !biometricStatus.isAvailable)
		{
			console.warn("Biometric authentication is not available on this device.");
			return null;
		}

		// 1. Check device security first
		// if (environment.production) {
		// 	const isRooted = await IsRoot.isRooted();
		// 	if (isRooted)
		// 	{
		// 		console.error("Device is rooted or jailbroken. Biometric authentication is not allowed.");
		// 		throw new Error('Device security compromised. Please use a secure device to access your account.');
		// 	}
		// }


		// 3. Get stored credentials (if needed for convenience)
		const credentials = await NativeBiometric.getCredentials({
			server
		});
		console.debug('Fetched biometric credentials:', credentials);
		return credentials;
	}

	public async setUserCredentialsWithBiometrics(username: string, password: string, server: string): Promise<void>
	{
		try
		{
			await NativeBiometric.setCredentials({
				server,
				username,
				password,
			});
		}
		catch (error)
		{
			console.error("Error setting biometric credentials", error);
			throw error; // Rethrow or handle as needed
		}
	}

	public async deleteUserCredentialsWithBiometrics(server: string): Promise<void>
	{
		try
		{
			await NativeBiometric.deleteCredentials({
				server,
			});
		}
		catch (error)
		{
			console.error("Error deleting biometric credentials", error);
			throw error; // Rethrow or handle as needed
		}
	}

	public biometryTypeLabel(biometryType: BiometryType): string
	{
		/*
		export declare enum BiometryType {
			NONE = 0,
			TOUCH_ID = 1,
			FACE_ID = 2,
			FINGERPRINT = 3,
			FACE_AUTHENTICATION = 4,
			IRIS_AUTHENTICATION = 5,
			MULTIPLE = 6,
			DEVICE_CREDENTIAL = 7
		}*/

		switch (biometryType)
		{
			case BiometryType.TOUCH_ID:
				return 'Touch ID';
			case BiometryType.FACE_ID:
				return 'Face ID';
			case BiometryType.FINGERPRINT:
				return 'Fingerprint';
			case BiometryType.FACE_AUTHENTICATION:
				return 'Face Authentication';
			case BiometryType.IRIS_AUTHENTICATION:
				return 'Iris Authentication';
			case BiometryType.MULTIPLE:
				return 'Multiple Biometric Types';
			case BiometryType.DEVICE_CREDENTIAL:
				return 'Device Credential (PIN/Pattern/Password)';
			case BiometryType.NONE:
			default:
				return 'Biometric Authentication';
		}
	}

	public async isBiometricCredentialsSaved(): Promise<boolean>
	{
		try
		{
			const result = await NativeBiometric.isCredentialsSaved({
				server: environment.application.baseUrl ?? 'healthmax.co.nz',
			});
			return result && result.isSaved;
		}
		catch (error)
		{
			console.error("Error checking if biometric credentials are saved", error);
			return false;
		}
	}
}